import React from 'react';
import { Col, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import InputLabel from 'components/layout/forms/InputLabel';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import translations from 'services/translations/translations';
import { H } from 'services/enums/units';
import { useCurrency } from 'services/offers/currency';
import { getWorkMonths, getWorkWeeks } from 'services/offers/workDays';

import { getContractsTotal } from './getVersionInstallationContract';
import IVersion from '../IVersion';

/**
 * The total of the summary rows: the installation contract and the sections of
 * subcontracting and own production in use. The sums of their expenses, contracts, selling
 * prices, margins, hours, workers and time, and the average hourly earnings of all the hours.
 */
const ContractsTotal = ({
  quantity,
  sections,
  version,
}: {
  quantity: number;
  // The sections in use besides the installation contract
  sections: string[];
  version: IVersion;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const currency = useCurrency();
  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories
  );

  const total = getContractsTotal(
    version,
    quantity,
    installationCategories,
    sections
  );

  const label = (key: string, average = false) => (
    <Col>
      <InputLabel>
        {t(`${translations.calculationWindow}:${key}`)}
        {average
          ? ` ${t(`${translations.calculationWindow}:average-short`)}`
          : ''}
      </InputLabel>
    </Col>
  );

  const field = (id: string, value: number, unit: string) => (
    <Col>
      <NumberWithUnitInputField
        disabled
        id={id}
        unit={unit}
        value={formatGroupedNumber(value)}
      />
    </Col>
  );

  return (
    <div className="calculation-sum-row calculation-contracts-total">
      <h2>{t(`${translations.calculationWindow}:Contracts total`)}</h2>
      <Row>
        {label('Contract price')}
        <Col>
          <InputLabel>
            {`${t(`${translations.calculationWindow}:Contracts`)} ${currency}`}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>{t(`${translations.offers}:Selling price`)}</InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} ${currency}`}
          </InputLabel>
        </Col>
        {label('average-hourly-earnings-short', true)}
        {label('Total hours')}
        {label('Installers')}
        {label('Work days')}
        {label('Weeks')}
        {label('Months')}
        <Col className="key-figure">
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} ${currency}`}/
            {t(`${translations.common}:units:${H}`)}
          </InputLabel>
        </Col>
      </Row>
      <Row>
        {field('contracts-total-expense', total.expense, currency)}
        {field('contracts-total-contract', total.contract, currency)}
        {field('contracts-total-selling-price', total.sellingPrice, currency)}
        {field('contracts-total-margin', total.margin, currency)}
        {field(
          'contracts-total-average-hourly-earnings',
          total.averageHourlyEarnings,
          currency
        )}
        {field('contracts-total-hours', total.hours, H)}
        {field(
          'contracts-total-installers',
          total.installers,
          t(`${translations.calculationWindow}:installers-short`)
        )}
        {field(
          'contracts-total-work-days',
          total.workDays,
          t(`${translations.calculationWindow}:work-days-short`)
        )}
        {field(
          'contracts-total-weeks',
          getWorkWeeks(total.workDays),
          t(`${translations.calculationWindow}:weeks-short`)
        )}
        {field(
          'contracts-total-months',
          getWorkMonths(total.workDays),
          t(`${translations.calculationWindow}:months-short`)
        )}
        <Col className="key-figure">
          <NumberWithUnitInputField
            disabled
            id="contracts-total-margin-for-h"
            unit={currency}
            value={formatGroupedNumber(
              total.hours > 0 ? total.margin / total.hours : 0
            )}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ContractsTotal;
